import React from "react";

import styled from "styled-components";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { flexRender } from "@tanstack/react-table";

const TableHeadTh = styled.th`
    position: sticky;
    top: 0;
    height: 32px;
    z-index: 1;
    border-bottom: solid 1px var(--color-Outline);

    &:last-child {
        border-right: none;
    }

    .sortHandle,
    .resizeHandle {
        visibility: hidden;
    }

    .sort-active {
        visibility: visible;
    }

    &:hover {
        .sortHandle,
        .resizeHandle {
            visibility: visible;
        }
    }
`;

const TableHeadThTitle = styled.div`
    display: flex;
    justify-content: start;
    align-items: center;
    cursor: ${(props) => props.cursor};
    padding-left: 4px;

    &:hover {
        border-radius: 2px;
        background-color: #aaccc6;
    }
`;

const TableHeadThSortHandle = styled.div`
    cursor: pointer;
    font-size: 16px;
    line-height: 16px;
    margin-left: 4px;
`;

const ResizeHandle = styled.div`
    position: absolute;
    top: 8px;
    right: 0;
    width: 4px;
    height: 20px;
    background-color: #084e45;
    cursor: col-resize;
    user-select: none;
`;

const Th = ({ header, useSortingColumns, useOrderColumns, useSizingColumns, nextHeader, onResizeColumns }) => {
    const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
        id: header.column.id,
    });

    const isDraggable = !["id", "is_favorite"].includes(header.column.id);
    const draggableProps = isDraggable
        ? useOrderColumns
            ? { ...attributes, ...listeners, style: { cursor: "grab" } }
            : {}
        : {};

    const style = {
        opacity: isDragging ? 0.8 : 1,
        padding: "8px 6px",
        backgroundColor: "var(--color-ListHead)",
        transform: CSS.Translate.toString(transform),
        transition: "width transform 0.2s ease-in-out",
        whiteSpace: "nowrap",
        textAlign: "left",
        zIndex: isDragging ? 2 : 1,
        width: header.column.getSize(),
    };

    const handleCustomResize = (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const initialWidth = header.column.getSize();
        const nextInitialWidth = nextHeader?.column.getSize() || 0;
        const minWidth = header.column.columnDef.minSize || 40;
        const nextMinWidth = nextHeader?.column.columnDef.minSize || 40;

        const onMouseMove = (moveEvent) => {
            const delta = moveEvent.clientX - startX;
            let newWidth = initialWidth + delta;
            let newNextWidth = nextInitialWidth - delta;

            if (newWidth < minWidth) {
                newWidth = minWidth;
                newNextWidth = initialWidth + nextInitialWidth - minWidth;
            } else if (newNextWidth < nextMinWidth) {
                newNextWidth = nextMinWidth;
                newWidth = initialWidth + nextInitialWidth - nextMinWidth;
            }

            if (onResizeColumns && nextHeader) {
                onResizeColumns(header.column.id, newWidth, nextHeader.column.id, newNextWidth);
            }
        };

        const onMouseUp = () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    return (
        <TableHeadTh key={header.id} colSpan={header.colSpan} ref={setNodeRef} style={style}>
            {!header.isPlaceholder && (
                <TableHeadThTitle {...draggableProps}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {!["id", "is_favorite"].includes(header.column.id) && useSortingColumns && (
                        <TableHeadThSortHandle
                            className={`sortHandle ${
                                header.column.getIsSorted() &&
                                !["id", "is_favorite"].includes(header.column.id) &&
                                "sort-active"
                            }`}
                            onMouseDown={
                                ["id", "is_favorite"].includes(header.column.id)
                                    ? undefined
                                    : header.column.getToggleSortingHandler()
                            }
                            title={
                                header.column.getCanSort() && !["id", "is_favorite"].includes(header.column.id)
                                    ? header.column.getNextSortingOrder() === "asc"
                                        ? "오름차순으로 정렬"
                                        : header.column.getNextSortingOrder() === "desc"
                                        ? "내림차순으로 정렬"
                                        : "정렬 해제"
                                    : undefined
                            }
                        >
                            {{
                                asc: "↑",
                                desc: "↓",
                            }[header.column.getIsSorted()] ?? "↕"}
                        </TableHeadThSortHandle>
                    )}
                </TableHeadThTitle>
            )}
            {!["id", "is_favorite", "scienceon_cn"].includes(header.column.id) && useSizingColumns && (
                <ResizeHandle
                    className={"resizeHandle"}
                    onDoubleClick={() => header.column.resetSize()}
                    onMouseDown={nextHeader ? handleCustomResize : header.getResizeHandler()}
                    onTouchStart={nextHeader ? handleCustomResize : header.getResizeHandler()}
                >
                    &nbsp;
                </ResizeHandle>
            )}
        </TableHeadTh>
    );
};

export default Th;
